import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { api, type UserView } from '../../api/client';
import { qk } from '../../api/hooks';
import { EmptyState, ErrorAlert, QueryBoundary } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { GesellschaftSelect, useGesellschaftNames } from './GesellschaftSelect';

export function UsersTab() {
  const [edit, setEdit] = useState<UserView | null>(null);
  const names = useGesellschaftNames();
  const q = useQuery({ queryKey: qk.users, queryFn: api.listUsers });
  const items = q.data ?? [];
  return (
    <>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, maxWidth: 860 }}>
        Rollen (Viewer, Operator, Admin) werden in Entra über die Autopilot-App vergeben. Hier legen Sie fest, welche Gesellschaften ein Nutzer sehen und bearbeiten darf – eine Rolle allein gibt keinen Zugriff auf Gesellschaften.
      </Typography>
      <Card>
        <QueryBoundary query={q} action="Nutzer laden" loadingRows={4}>
          {items.length === 0 ? (
            <EmptyState title="Noch keine Nutzer" description="Nutzer erscheinen nach ihrer ersten Anmeldung." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table aria-label="Nutzer und Gesellschaftsscopes" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Rollen</TableCell>
                    <TableCell>Gesellschaften</TableCell>
                    <TableCell>Zuletzt aktiv</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {u.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {u.email ?? '–'}
                        </Typography>
                      </TableCell>
                      <TableCell>{u.roles.join(', ') || '–'}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>{u.gesellschaftIds.length ? names(u.gesellschaftIds) : <em>keine</em>}</TableCell>
                      <TableCell>{formatDateTime(u.lastSeenAt)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setEdit(u)}>
                          Scope bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
      </Card>
      {edit && <ScopeDialog user={edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function ScopeDialog({ user, onClose }: { user: UserView; onClose: () => void }) {
  const id = useId();
  const qc = useQueryClient();
  const [gs, setGs] = useState(user.gesellschaftIds);
  const save = useMutation({
    mutationFn: () => api.setUserScope(user.id, gs, user.rowVersion ?? 0),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} aria-labelledby={id} maxWidth="sm" fullWidth>
      <DialogTitle id={id}>Gesellschaften für {user.name}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <GesellschaftSelect value={gs} onChange={setGs} helperText="Leer = keine Gesellschaft (kein Datenzugriff)." />
        {save.error ? <ErrorAlert error={save.error} action="Scope speichern" /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
